import { useEffect, useState } from "react";
import { VersionedEmailBlock, UserInvite, MailboxItem, MailMessageResponse, MailMessageV4, IFetchResponse, MessageSendMetadata } from "@preveil-api";
import { InviteMetadata, MailInviteMetadata } from "src/common/keys/protos/invite_pb";
import {
  MessageV4, WebMailData, MessageLatest, Account, MailFlags, isSameUser, FileSizeLimits, Helpers, CURRENT_EMAIL_PROTOCOL_VERSIONS, DefaultMailboxes,
  getMailboxCollectionIdByRole, usePostStorageByCollectionIdBlocksMutation, PostStorageByCollectionIdBlocksApiResponse, usePostMailSendMutation, usePutUsersInviteMutation, isProtocolV4, usePatchMailByCollectionIdMailboxesAndMbidMessagesMutation, usePostMailByCollectionIdMailboxesAndMbidMessagesMutation, usePostMailByCollectionIdMutation, Message,
  useAppDispatch, MessageToastTypes, AppConfiguration, InviteStatus
} from "src/common";
import { uiActions } from "src/store";
import _ from "lodash";


export function useSendWebMail(current_account: Account, send_metadata?: MessageSendMetadata, mail_data?: WebMailData[], default_mailboxes?: MailboxItem[]) {
  const dispatch = useAppDispatch();
  const is_web = AppConfiguration.buildForWeb();
  const protocol_version = getMessageProtocolVersion();
  const [errors, setErrors] = useState<IFetchResponse[]>();
  const [sendInvites] = usePutUsersInviteMutation();
  const [postBlocks] = usePostStorageByCollectionIdBlocksMutation();
  const [sendMailLatest, { isLoading, data }] = usePostMailSendMutation();
  const [updateFlags] = usePatchMailByCollectionIdMailboxesAndMbidMessagesMutation(); // FOR ALL MAIL VERSIONS
  const [sendMailV4] = usePostMailByCollectionIdMutation();
  const [appendToMailbox] = usePostMailByCollectionIdMailboxesAndMbidMessagesMutation(); // ONLY FOR V4 mails

  // Description: On webData receive send the blocks up to storage
  // On Success send invite to unclaimed users AFTER getting the server message id
  // On Success also update flags
  // DEBUGGING NOTES: use debugPrivateMetadata(current_account, mail_data[0].metadata.message as MailMessageLatest); for debugging private metadata
  useEffect(() => {
    if (!!mail_data) {
      handleSendWebMail(mail_data);
    }
  }, [mail_data]);

  // Description: Send / Post data Blocks to storage first
  // On success: send invites with the server-assigned message id, then update flags
  async function handleSendWebMail(_web_data: WebMailData[]) {
    const results = await handleAllBlocks(_web_data);
    // Examine for errors - if none, send invites (with server id) then update flags
    const errors = _.filter(results, (result: { result?: MailMessageResponse; error?: IFetchResponse }) => (!!result.error));
    if (!!errors && errors.length > 0) {
      handleMailSendError(errors as IFetchResponse[]);
    } else {
      // Extract the server-assigned message id from the send response
      const server_message_id = _.find(results, r => !!r.result?.id)?.result?.id;
      if (!!send_metadata?.unclaimed && send_metadata.unclaimed.length > 0 && !!server_message_id) {
        await handleSendInvites(send_metadata.unclaimed, server_message_id);
      }
      handleUpdateFlags();
    }
  }

  // Description: Iterate through all WebMailData and breakdown blocks at FileSizeLimits.MAIL_MAX_BLOCKS_PER_REQUEST
  // On successful putBlocks send mail by protocol version
  async function handleAllBlocks(_web_data: WebMailData[]): Promise<Array<{ result?: MailMessageResponse; error?: unknown }>> {
    return await Promise.all(_.map(_web_data, async (data: WebMailData) => {
      const blocks = data.blocks;
      const blocks_per_request = FileSizeLimits.MAIL_MAX_BLOCKS_PER_UPLOAD;
      const chunks =
        [...Array(Math.ceil(blocks.length / blocks_per_request)).keys()]
          .map(i => blocks.slice(i * blocks_per_request, i * blocks_per_request + blocks_per_request));
      // NOTE: Iterate all calls and assemble blocks
      return await Promise.all(_.map(chunks, (block_chunk: VersionedEmailBlock[]) => {
        const collection_id = isProtocolV4(protocol_version) ? (data.metadata as MessageV4).recipient.mail_cid : current_account.mail_cid;
        return handlePostBlocks(block_chunk, collection_id);
      }))
        .then((responses: PostStorageByCollectionIdBlocksApiResponse[]) => {
          let result: Array<{ block_id: string }> = [];
          for (const i of responses) {
            result = result.concat(i.data);
          }
          return handleSendMessageByVersion(data)
            .then((result: MailMessageResponse) => ({ result }));
        })
        .catch((error: IFetchResponse) => {

          return { error };
        });
    }));
  };

  // Description: Handle Posting block chunks
  async function handlePostBlocks(_blocks: VersionedEmailBlock[], collection_id: string): Promise<PostStorageByCollectionIdBlocksApiResponse> {
    const blocks = _blocks.map(b => (Object.assign({}, b, { data: Helpers.b64Encode(b.data) })));
    return await postBlocks({
      account_ids: Account.getAccountIdentifiers(current_account),
      body: {
        collectionId: collection_id,
        blocks
      }
    }).unwrap()
      .then((response: PostStorageByCollectionIdBlocksApiResponse) => {
        return response;
      });
  }

  // Description:  Handle the send part
  async function handleSendMessageByVersion(data: WebMailData): Promise<MailMessageResponse> {
    if (isProtocolV4(protocol_version)) {
      const metadata = data.metadata as MessageV4;
      // NOTE: If recipient is the sender (needed for uploading the blocks) => update the Send mailbox; else send to recipient
      return (isSameUser(metadata.recipient.user_id, current_account.user_id)) ?
        await handleV4AppendToMailbox(metadata.message) :
        await handleSendMailV4(metadata);
    } else {
      return await handleSendMailLatest(data.metadata as MessageLatest);
    }
  }

  // Description: Send Mail to Collection Server after PUT BLOCKS (Latest versions)
  async function handleSendMailLatest(message: MessageLatest): Promise<MailMessageResponse> {
    return await sendMailLatest({
      account_ids: Account.getAccountIdentifiers(current_account),
      body: {
        message
      }
    }).unwrap()
      .then((response: MailMessageResponse) => {
        return response;
      });
  }

  // Description: Send Mail to Collection Server after PUT BLOCKS (Latest versions)
  // NOTES: Send mail is a single recipient send. It needs to loop through all recipients to send mail_data out
  async function handleSendMailV4(metadata: MessageV4): Promise<MailMessageResponse> {
    const message = metadata.message;
    const collection_id = metadata.recipient.mail_cid;
    return await sendMailV4({
      account_ids: Account.getAccountIdentifiers(current_account),
      body: {
        collectionId: collection_id, // THIS IS THE RECIPIENTS mail_cid!!!!!
        message
      }
    }).unwrap()
      .then((response: MailMessageResponse) => {
        return response;
      });
  }

  // Description: On V4 Mail Send Success need to call this one with the same message object 
  // NOTE:  For every message a user sends should put a copy of that message in the sender’s sent mailbox. 
  //        for V4 needs to be explicitly done.
  async function handleV4AppendToMailbox(message: MailMessageV4) {
    let result = null;
    const sent_mailbox_id = !!default_mailboxes ? getMailboxCollectionIdByRole(DefaultMailboxes.sent, default_mailboxes) : null;
    if (!!sent_mailbox_id) {
      result = await appendToMailbox({
        account_ids: Account.getAccountIdentifiers(current_account),
        body: {
          collectionId: current_account.mail_cid,
          mbid: sent_mailbox_id,
          message
        }
      }).unwrap()
        .then((response: MailMessageResponse) => {
          return response;
        });
      return result;
    } else {
      throw new Error("Failed sending message");
    }
  }

  // Description: On Success Update flags 
  function handleUpdateFlags() {
    const draft_mailbox_id = !!default_mailboxes ? getMailboxCollectionIdByRole(DefaultMailboxes.drafts, default_mailboxes) : null;
    if (!!draft_mailbox_id && !!send_metadata?.draft_id && !!send_metadata?.draft_version) {
      updateFlags({
        account_ids: Account.getAccountIdentifiers(current_account),
        body: {
          collectionId: current_account.mail_cid,
          mbid: draft_mailbox_id,
          updates: [{
            id: send_metadata.draft_id,
            last_version: send_metadata.draft_version,
            flags: [MailFlags.DELETED_FLAG, MailFlags.SEEN_FLAG, MailFlags.DRAFT_FLAG]
          }]
        }
      }).unwrap()
        .catch((error: IFetchResponse) => {
          handleMailSendError([error]);
        });
    }
  }

  // Description: Handle all errors
  function handleMailSendError(_errors: IFetchResponse[]) {
    setErrors(_errors);
  }

  // Description: Send invite to unclaimed users AFTER send succeeds, using the server-assigned message id;
  // collects all results before dispatching a single consolidated already_invited warning
  async function handleSendInvites(_unclaimed: UserInvite[], server_message_id: string) {
    const results = await Promise.all(
      _unclaimed.map(async (_body: UserInvite) => {
        // Build invite metadata with the correct server-assigned message id
        const invite_metadata = new InviteMetadata();
        invite_metadata.setInviteType(InviteMetadata.InviteType.MAIL);
        invite_metadata.setInvitee(_body.invitee_email.toLowerCase());
        const mail_metadata = new MailInviteMetadata();
        mail_metadata.setMessageId(server_message_id);
        invite_metadata.setMailMetadata(mail_metadata);
        const metadata = !is_web ? Helpers.b64Encode(invite_metadata.serializeBinary()) : undefined;
        const signature = !is_web ? Helpers.b64Encode(await current_account.user_key.signing_key.sign(invite_metadata.serializeBinary())) : undefined;
        const body: UserInvite = { user_id: _body.user_id, invitee_email: _body.invitee_email, metadata, signature };
        return await sendInvites({
          account_ids: Account.getAccountIdentifiers(current_account),
          body
        }).unwrap()
          .catch((error: IFetchResponse) => {
            handleMailSendError([error]);
            return null;
          });
      })
    );
    const already_invited = _unclaimed.filter((_, i) => results[i]?.status === InviteStatus.already_invited);
    if (already_invited.length === 1) {
      dispatch(uiActions.handleSetMessage(new Message(`${already_invited[0].invitee_email} was already invited so no further invite email will be sent.`, undefined, MessageToastTypes.warning)));
    } else if (already_invited.length > 1) {
      dispatch(uiActions.handleSetMessage(new Message(`${already_invited.length} users were already invited, no further invite emails will be sent.`, undefined, MessageToastTypes.warning)));
    }
  }

  // Description: Get protocol_version from message or default to current
  function getMessageProtocolVersion() {
    return !!mail_data ? mail_data[0].metadata.message.protocol_version : CURRENT_EMAIL_PROTOCOL_VERSIONS;
  };

  return {
    data,
    isLoading,
    errors
  };
}
